import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Heart, User, ShoppingBag, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useShop } from "@/context/ShopContext";
import { NAV } from "@/lib/content";

export const Logo = ({ light }) => (
  <Link to="/" data-testid="header-logo" className="group flex flex-col leading-none" aria-label="Kurtaani home">
    <span className={`font-serif-display text-[28px] font-semibold tracking-[0.06em] sm:text-[32px] ${light ? "text-[#FAF7F2]" : "text-[#701A24]"}`}>
      Kurtaani<span className="text-[#D4AF37]">.</span>
    </span>
    <span className={`mt-0.5 text-[9px] uppercase tracking-[0.42em] ${light ? "text-[#D99B8E]" : "text-[#B85D43]"}`}>ethnic · everyday</span>
  </Link>
);

const IconBtn = ({ children, badge, ...p }) => (
  <button {...p} className="relative rounded-full p-2.5 text-[#1C1917] transition-colors hover:bg-[#EAE0D5] hover:text-[#701A24]">
    {children}
    {badge > 0 && (
      <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#B85D43] px-1 text-[10px] font-bold text-white">{badge}</span>
    )}
  </button>
);

const SearchDialog = ({ open, onOpenChange }) => {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const go = (e) => {
    e.preventDefault();
    onOpenChange(false);
    nav(`/collections/all?q=${encodeURIComponent(q)}`);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-3xl bg-[#FAF7F2]">
        <DialogTitle className="font-serif-display text-2xl text-[#701A24]">What are you looking for?</DialogTitle>
        <form onSubmit={go} className="mt-2 flex gap-2">
          <input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Try ‘chikankari’, ‘anarkali’, ‘cotton’" className="flex-1 rounded-full border border-[#E7E2DB] bg-white px-5 py-3 text-sm outline-none focus:border-[#B85D43]" />
          <button data-testid="search-submit-button" className="btn-primary px-5">Search</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Header() {
  const { count, wishlist, setCartOpen } = useShop();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const nav = useNavigate();
  const linkCls = ({ isActive }) =>
    `relative py-1 text-[13px] font-medium tracking-wide transition-colors after:absolute after:-bottom-0.5 after:left-0 after:h-px after:bg-[#B85D43] after:transition-all ${isActive ? "text-[#701A24] after:w-full" : "text-[#44403C] after:w-0 hover:text-[#701A24] hover:after:w-full"}`;

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 border-b border-[#E7E2DB] bg-[#FAF7F2]/90 backdrop-blur-md">
      <div className="kt-container flex h-[72px] items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button data-testid="mobile-menu-button" onClick={() => setMenu(true)} className="rounded-full p-2 lg:hidden" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
          <Logo />
        </div>
        <nav className="hidden items-center gap-6 lg:flex">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>
              <span className={n.label === "Sale" ? "text-[#B85D43]" : ""}>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center">
          <IconBtn data-testid="header-search-button" aria-label="Search" onClick={() => setSearch(true)}><Search className="h-5 w-5" /></IconBtn>
          <IconBtn data-testid="header-wishlist-button" aria-label="Wishlist" badge={wishlist.length} onClick={() => nav("/wishlist")}><Heart className="h-5 w-5" /></IconBtn>
          <span className="hidden sm:inline-flex"><IconBtn data-testid="header-account-button" aria-label="Account / Track order" onClick={() => nav("/pages/track-order")}><User className="h-5 w-5" /></IconBtn></span>
          <IconBtn data-testid="header-cart-button" aria-label="Shopping bag" badge={count} onClick={() => setCartOpen(true)}><ShoppingBag className="h-5 w-5" /></IconBtn>
        </div>
      </div>
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" className="w-[86%] bg-[#FAF7F2] sm:max-w-sm">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Logo />
          <div className="mt-8 flex flex-col">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMenu(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className="border-b border-[#E7E2DB] py-4 font-serif-display text-2xl text-[#1C1917] hover:text-[#701A24]">
                {n.label}
              </Link>
            ))}
            <Link to="/combo" onClick={() => setMenu(false)} data-testid="mobile-nav-combo" className="border-b border-[#E7E2DB] py-4 font-serif-display text-2xl text-[#701A24]">2 for ₹329 Combo</Link>
            <Link to="/navratri-couple-edit" onClick={() => setMenu(false)} data-testid="mobile-nav-navratri-edit" className="border-b border-[#E7E2DB] py-4 font-serif-display text-2xl text-[#701A24]">Navratri Couple Edit</Link>
            <Link to="/pages/track-order" onClick={() => setMenu(false)} className="py-4 text-sm font-medium text-[#B85D43]">Track your order</Link>
          </div>
        </SheetContent>
      </Sheet>
      <SearchDialog open={search} onOpenChange={setSearch} />
    </header>
  );
}
